package com.anjuros.website.redesign.styles

import com.anjuros.website.redesign.theme.RdColor
import com.anjuros.website.redesign.theme.RdFont
import com.anjuros.website.redesign.theme.RdSize
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.percent

// ---------- Hero: founder profile card ----------

val AboutHeroGridStyle = CssStyle {
    base { grid("1fr").alignItems(AlignItems.Center).css("gap" to "clamp(32px, 5vw, 72px)") }
    Breakpoint.LG { Modifier.css("grid-template-columns" to "1.2fr .8fr") }
}

val AboutProfileStyle = CssStyle.base {
    Modifier.backgroundColor(RdColor.Paper).border(1.px, LineStyle.Solid, RdColor.Line).borderRadius(20.px)
        .css("overflow" to "hidden", "min-width" to "0", "max-width" to "420px", "width" to "100%", "justify-self" to "center", "box-shadow" to RdSize.SHADOW)
}

val AboutProfileTopStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Grid).backgroundColor(RdColor.SkySoft)
            .css("place-items" to "center", "padding" to "28px 20px 0", "border-bottom" to "1px solid rgb(180,198,220)")
    }
}

/**
 * Both avatar pictures sit on top of each other; the closed-eyes one shows while the pointer is on the avatar.
 * Nothing is reloaded on hover, so the swap never flickers.
 */
val AboutAvatarStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).css("width" to "min(220px, 70%)") }
    cssRule(" img") { Modifier.display(DisplayStyle.Block).css("grid-area" to "1 / 1", "width" to "100%", "height" to "auto") }
    cssRule(" img:last-child") { Modifier.css("opacity" to "0") }
    cssRule(":hover img:last-child") { Modifier.css("opacity" to "1") }
}

val AboutProfileBodyStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Grid).gap(14.px).css("padding" to "20px 22px 22px")
}

val AboutProfileNameStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(4.px) }
    cssRule(" b") { Modifier.fontFamily(*RdFont.DISPLAY).css("font-size" to "1.3rem", "font-weight" to 800, "font-stretch" to "110%", "line-height" to 1.15) }
    cssRule(" span") { Modifier.color(RdColor.Muted).css("font-size" to ".95rem") }
}

val AboutProfileFactsStyle = CssStyle {
    base {
        // Labels and values share a baseline, so the small caps labels line up with the text beside them.
        grid("auto 1fr").css("gap" to "8px 16px", "align-items" to "baseline", "margin" to "0", "font-size" to ".9rem", "padding-top" to "14px", "border-top" to "1px solid rgb(220,227,236)")
    }
    cssRule(" dt") {
        Modifier.color(RdColor.Muted).css("font" to "500 .7rem/1.6 ${RdFont.MONO_STACK}", "letter-spacing" to ".08em", "text-transform" to "uppercase")
    }
    cssRule(" dd") { Modifier.color(RdColor.Ink).css("margin" to "0") }
}

val AboutProfileLinksStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Flex).gap(10.px).css("flex-wrap" to "wrap") }
    cssRule(" a") { Modifier.css("padding" to "11px 16px", "font-size" to ".92rem") }
}

// ---------- Brand band ----------

val AboutBrandsStyle = CssStyle.base {
    Modifier.backgroundColor(RdColor.SkySoft).css("border-block" to "1px solid rgb(180,198,220)", "padding-block" to "clamp(26px, 4vw, 38px)")
}

val AboutBrandsRowStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Flex).alignItems(AlignItems.Center).justifyContent(JustifyContent.SpaceBetween)
        .css("flex-wrap" to "wrap", "gap" to "16px 48px")
}

val AboutBrandsLabelStyle = CssStyle.base {
    Modifier.color(RdColor.Brand).css(
        "font" to "500 .78rem/1.4 ${RdFont.MONO_STACK}", "letter-spacing" to ".12em", "text-transform" to "uppercase", "max-width" to "30ch"
    )
}

/** Brand names set as large text, not logos. */
val AboutBrandsListStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Flex).alignItems(AlignItems.Center).css("flex-wrap" to "wrap", "gap" to "12px 44px", "list-style" to "none", "padding" to "0") }
    cssRule(" li") {
        Modifier.color(RdColor.Muted).fontFamily(*RdFont.DISPLAY)
            .css("font-size" to "clamp(1.15rem, .9rem + 1vw, 1.6rem)", "font-weight" to 800, "font-stretch" to "112%", "line-height" to 1)
    }
}

// ---------- Story ----------

val AboutStoryGridStyle = CssStyle {
    base { grid("1fr").css("gap" to "clamp(32px, 5vw, 72px)", "align-items" to "start") }
    Breakpoint.LG { Modifier.css("grid-template-columns" to ".9fr 1.1fr") }
}

/** Large quote that stays in view while the story scrolls past on wide screens. */
val AboutQuoteStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(16.px).css("margin" to "0") }
    Breakpoint.LG { Modifier.position(Position.Sticky).css("top" to "calc(env(safe-area-inset-top, 0px) + 100px)") }
    cssRule(" p") {
        Modifier.fontFamily(*RdFont.DISPLAY).css(
            "font-size" to "clamp(1.5rem, 1rem + 1.8vw, 2.3rem)", "font-weight" to 800, "font-stretch" to "112%",
            "line-height" to 1.15, "letter-spacing" to "-.01em", "text-wrap" to "balance"
        )
    }
    cssRule(" p::before") {
        Modifier.color(RdColor.LogoBlue).css("content" to "\"“\"", "display" to "block", "font-size" to "3.2em", "line-height" to .6, "margin-bottom" to "6px")
    }
    cssRule(" figcaption") { Modifier.color(RdColor.Muted).css("font-size" to ".92rem") }
}

val AboutStoryTextStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(28.px).css("min-width" to "0") }
    cssRule(" > div") { Modifier.display(DisplayStyle.Grid).gap(10.px) }
    cssRule(" p") { Modifier.color(RdColor.Muted).css("max-width" to "62ch") }
}

// ---------- Principles ----------

val AboutPrinciplesGridStyle = CssStyle {
    base { grid("1fr").gap(20.px).css("list-style" to "none", "padding" to "0") }
    Breakpoint.SM { Modifier.css("grid-template-columns" to "1fr 1fr") }
    Breakpoint.LG { Modifier.css("grid-template-columns" to "repeat(3, 1fr)") }
    cssRule(" li") {
        Modifier.display(DisplayStyle.Grid).gap(10.px).backgroundColor(RdColor.Paper).border(1.px, LineStyle.Solid, RdColor.Line)
            .borderRadius(16.px).css("align-content" to "start", "padding" to "24px")
    }
    cssRule(" p") { Modifier.color(RdColor.Muted).css("font-size" to ".96rem") }
}

// ---------- Services ----------

val AboutServicesGridStyle = CssStyle {
    base { grid("1fr").css("row-gap" to "20px", "border-top" to "2px solid rgb(30,40,52)") }
    Breakpoint.SM { Modifier.css("grid-template-columns" to "1fr 1fr") }
    Breakpoint.LG { Modifier.css("grid-template-columns" to "repeat(4, 1fr)") }
}

val AboutServiceStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Grid).gap(10.px).color(RdColor.Ink).textDecorationLine(TextDecorationLine.None)
            .css("align-content" to "start", "padding" to "22px 22px 6px 0")
    }
    cssRule(" p") { Modifier.color(RdColor.Muted).css("font-size" to ".95rem") }
    cssRule(" span") { Modifier.color(RdColor.Brand).css("font-weight" to 600, "font-size" to ".95rem") }
    cssRule(":hover h3") { Modifier.color(RdColor.Brand) }
}

// ---------- Real client project ----------

val AboutWorkCardStyle = CssStyle {
    base {
        grid("1fr").backgroundColor(RdColor.Paper).border(1.px, LineStyle.Solid, RdColor.Line).borderRadius(20.px)
            .css("overflow" to "hidden")
    }
    Breakpoint.LG { Modifier.css("grid-template-columns" to "1.1fr .9fr") }
}

/** Laptop and phone screenshots side by side on the pale-blue panel, never overlapping. */
val AboutWorkShotsStyle = CssStyle {
    base {
        grid("1fr 30%").alignItems(AlignItems.Center).gap(12.px).backgroundColor(RdColor.SkySoft)
            .css("padding" to "clamp(16px, 3vw, 28px)", "border-bottom" to "1px solid rgb(180,198,220)", "min-width" to "0")
    }
    Breakpoint.LG { Modifier.css("border-bottom" to "0", "border-right" to "1px solid rgb(180,198,220)") }
    cssRule(" img") { Modifier.display(DisplayStyle.Block).css("width" to "100%", "height" to "auto", "mix-blend-mode" to "multiply") }
    // The laptop screenshot has empty space above and below the device, so crop to the laptop.
    cssRule(" img:first-child") { Modifier.css("aspect-ratio" to "1000 / 620", "object-fit" to "cover") }
}

val AboutWorkCopyStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(18.px).css("align-content" to "center", "padding" to "clamp(24px, 4vw, 44px)", "min-width" to "0") }
    cssRule(" > a") { Modifier.css("justify-self" to "start") }
}

val AboutWorkTopStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Flex).alignItems(AlignItems.Center).justifyContent(JustifyContent.SpaceBetween).gap(10.px)
        .css("flex-wrap" to "wrap")
}

/** "Live" label with a green dot. */
val AboutWorkStatusStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Flex).alignItems(AlignItems.Center).gap(7.px).color(RdColor.Brand).backgroundColor(RdColor.SkySoft)
            .border(1.px, LineStyle.Solid, RdColor.LogoBlue).borderRadius(999.px)
            .css("font" to "500 .7rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".08em", "text-transform" to "uppercase", "padding" to "7px 10px")
    }
    cssRule("::before") {
        Modifier.borderRadius(50.percent).css("content" to "\"\"", "width" to "7px", "height" to "7px", "background" to "rgb(47,143,91)")
    }
}

val AboutWorkFactsStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(12.px).css("margin" to "0", "padding-top" to "16px", "border-top" to "2px solid rgb(30,40,52)") }
    cssRule(" dt") {
        Modifier.color(RdColor.Brand)
            .css("font" to "500 .72rem/1.7 ${RdFont.MONO_STACK}", "letter-spacing" to ".08em", "text-transform" to "uppercase")
    }
    cssRule(" dd") { Modifier.color(RdColor.Muted).css("margin" to "0") }
}

val AboutWorkFactsRowStyle = CssStyle {
    base { grid("1fr").gap(2.px) }
    // Side by side, the label shares a baseline with the first line of its text.
    Breakpoint.SM { Modifier.css("grid-template-columns" to "100px 1fr", "gap" to "12px", "align-items" to "baseline") }
}

val AboutWorkTagsStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Flex).gap(8.px).css("flex-wrap" to "wrap", "list-style" to "none", "margin" to "0", "padding" to "0") }
    cssRule(" li") {
        Modifier.backgroundColor(RdColor.Ground).border(1.px, LineStyle.Solid, RdColor.Line).borderRadius(999.px)
            .css("font-size" to ".86rem", "font-weight" to 500, "padding" to "6px 11px")
    }
}
